import { remote, supertypeClass } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types/index';
import { Policy } from '../../../common/js/customer/Policy';
import { GiactWebDebitRequest, GiactWebDebitResponse, GiactResponseCodes } from './../interfaces/CoverpathTypes';
import _ from 'lodash';
import { VerifyGiactWebDebitClient } from './../../../common/js/wsclient/verifyGiactWebDebit/VerifyGiactWebDebitClient';
import { CoverpathBasePolicy } from './../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { GiactUtils } from '../../../common/js/GiactUtils';
import { LoggerSingleton, Category } from '../../../common/js/logger/LoggerSingleton';

export interface BankingInfo {
  paymentABA: string;
  paymentAccount: string;
}

@supertypeClass({ toClient: true, toServer: true })
export class GiactController extends AppController {
  static new() {
    return new GiactController().init();
  }

  init() {
    return this;
  }

  @remote({ on: 'server' })
  public async verifyGiactWebDebit(
    policy: Policy,
    giactLdFlagJsonValue,
    userSource: string,
    newValues: BankingInfo = null
  ): Promise<GiactWebDebitResponse> {
    const requestPayload: GiactWebDebitRequest = {
      referenceId: policy.application.applicationNumber,
      referenceType: 'ApplicationNumber',
      productCode: policy.product,
      userSource: userSource,
      channel: policy.getChannelCode(),
      routingNumber: !_.isEmpty(newValues) ? newValues.paymentABA : policy.paymentABA,
      accountNumber: !_.isEmpty(newValues) ? newValues.paymentAccount : policy.paymentAccount,
    };
    LoggerSingleton.getInstance().info(
      {
        module: 'GiactController',
        function: 'verifyGiactWebDebit',

        data: {
          payload: {
            referenceId: requestPayload.referenceId,
            referenceType: requestPayload.referenceType,
            productCode: requestPayload.productCode,
            userSource: requestPayload.userSource,
            channel: requestPayload.channel,
          },
        },
        category: Category.request,
      },
      'Payment information(GIACT) verification'
    );

    if (!_.isEmpty(giactLdFlagJsonValue)) {
      const response = await VerifyGiactWebDebitClient.invokeVerifyCall(policy, requestPayload);
      if (!response.statusCode) {
        if (GiactUtils.isLetterFailureCode(response.normalizedResponseCode)) {
          (policy.application.primaryPolicy as CoverpathBasePolicy).giactLetterLastFour =
            newValues.paymentAccount.substring(newValues.paymentAccount.length - 4);
          policy.application.fulfillFailureNotificationRequirements(['FN1R1']);
        }
        return response;
      } else {
        if (GiactUtils.isGiactBlockingId(response.id)) {
          LoggerSingleton.getInstance().error(
            {
              module: 'GiactController',
              function: 'verifyGiactWebDebit',

              category: Category.request,
              error: {
                isHumanRelated: false,
              },
            },
            response
          );
          return {
            status: 'fail',
            normalizedResponseCode: GiactResponseCodes.INVALID,
            sourceResponseCode: GiactResponseCodes.INVALID,
          };
        }
        return {
          status: 'pass',
          normalizedResponseCode: response.statusCode,
          sourceResponseCode: response.statusCode,
        };
      }
    }
    return {
      status: 'pass',
      normalizedResponseCode: GiactResponseCodes.SERVICE_OFF,
      sourceResponseCode: GiactResponseCodes.SERVICE_OFF,
    };
  }

  @remote({ on: 'server' })
  public updateLastWebDebitAttemptCombination(policy: CoverpathBasePolicy, lastWebDebitAttemptCombination: string) {
    policy.lastWebDebitAttemptCombination = lastWebDebitAttemptCombination;
  }

  @remote({ on: 'server' })
  public updatePaymentGiactInfo(policy: CoverpathBasePolicy, giactReponse: GiactWebDebitResponse) {
    policy.giactWebDebitResponseCode = giactReponse.normalizedResponseCode;
    policy.isGiactWebDebitValid = giactReponse.status ? giactReponse.status === 'pass' : null;
  }
}
